/**
 * <predictive-search> custom element.
 *
 * Fetches the global-header section from Shopify's Section Rendering API
 * and swaps the inner content of [data-predictive-search-content] between
 * the empty state (trending + collections) and the results state (products).
 *
 * Respects two toggles:
 *  - theme.settings.predictiveSearchEnabled (window.theme.settings)
 *  - the section's `enable_predictive_search` setting, surfaced via the
 *    `data-enable-predictive` attribute on this element.
 *
 * Search & Discovery synonyms/boosts are applied automatically by Shopify
 * when the predictive_search object is populated server-side.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.emptyStateMarkup = null;
    this.isOpen = false;
    this.loadingText = this.getAttribute('data-loading-text') || '';
    this._transitionGeneration = 0;
    this._contentTransition = null;
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.contentEl = this.querySelector('[data-predictive-search-content]');
    this.statusElement = this.querySelector('.predictive-search-status');

    if (this.contentEl && this.emptyStateMarkup == null) {
      this.emptyStateMarkup = this.contentEl.innerHTML;
    }

    if (!this._eventsBound) {
      this.setupEventListeners();
      this._eventsBound = true;
    }
  }

  /**
   * Checks both the theme-level toggle and a per-section opt-out attribute.
   * The section attribute lives on [data-predictive-search-content] so it
   * is captured on the initial server-side render and persists across
   * subsequent inner-HTML swaps.
   */
  isPredictiveEnabled() {
    const contentFlag =
      this.contentEl &&
      this.contentEl.dataset &&
      this.contentEl.dataset.enablePredictive;

    if (contentFlag === 'false') return false;

    const themeSettings =
      window.theme && window.theme.settings ? window.theme.settings : {};

    if (themeSettings.predictiveSearchEnabled === false) return false;

    return true;
  }

  setupEventListeners() {
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce(this.onChange.bind(this), 300)
      );
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onChipClick.bind(this));
  }

  onChipClick(event) {
    const chip = event.target.closest('[data-search-term]');
    if (!chip || !this.input) return;

    event.preventDefault();
    this.applySearchTerm(chip.dataset.searchTerm || '');
  }

  applySearchTerm(searchTerm) {
    if (!this.input) return;

    const term = searchTerm.trim();
    this.input.value = term;

    if (!term.length) {
      this.restoreEmptyState();
      return;
    }

    if (this.isPredictiveEnabled()) {
      this.getSearchResults(term);
      return;
    }

    const form = this.querySelector('form');
    if (form) form.submit();
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.restoreEmptyState();
      return;
    }

    if (!this.isPredictiveEnabled()) return;

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      if (!this.getQuery().length) event.preventDefault();
    }
  }

  onFocus() {
    this.classList.add('is-focused');

    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else if (this.isPredictiveEnabled()) {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.input) return;

    if (!this.getQuery().length) this.restoreEmptyState();

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allOptions = this.querySelectorAll('[role="option"]');

    if (!allOptions.length) return;

    let activeElement = allOptions[0];

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allOptions[0];
    } else if (moveUp && selectedElement) {
      activeElement =
        selectedElement.previousElementSibling ||
        allOptions[allOptions.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.setLiveRegionText(activeElement.textContent);
    if (activeElement.id) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }

  selectOption() {
    const selected = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&${encodeURIComponent('resources[type]')}=product,query` +
      `&${encodeURIComponent('resources[limit]')}=10` +
      `&${encodeURIComponent('resources[fields]')}=title,product_type,variants.title,vendor,tag` +
      `&section_id=global-header`;

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const sectionEl = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-global-header');

        if (!sectionEl) {
          this.showFetchError();
          return;
        }

        const innerContent = sectionEl.querySelector(
          '[data-predictive-search-content]'
        );
        const resultsMarkup = innerContent
          ? innerContent.innerHTML
          : sectionEl.innerHTML;

        if (!resultsMarkup.trim()) {
          this.showFetchError();
          return;
        }

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch(() => {
        this.showFetchError();
      });
  }

  showFetchError() {
    this.removeAttribute('loading');
    if (!this.contentEl) return;

    this.swapContent(
      '<p class="predictive-search-results__empty text-size-small">Unable to load results. Try again or press Enter to search.</p>'
    ).then(() => {
      this.setAttribute('results', 'true');
      this.open();
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  isDesktopViewport() {
    return window.matchMedia('(min-width: 930px)').matches;
  }

  getPanelInner() {
    return this.contentEl
      ? this.contentEl.closest('.search-modal__panel-inner')
      : null;
  }

  waitForOpacityTransition(element) {
    return new Promise(resolve => {
      if (this.prefersReducedMotion()) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        element.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallbackTimer);
        resolve();
      };

      const onTransitionEnd = event => {
        if (event.target !== element || event.propertyName !== 'opacity') return;
        finish();
      };

      const fallbackTimer = window.setTimeout(finish, 350);
      element.addEventListener('transitionend', onTransitionEnd);
    });
  }

  lockPanelInnerHeight(panelInner) {
    if (!panelInner || !this.isDesktopViewport()) return null;

    const lockedHeight = panelInner.offsetHeight;
    panelInner.classList.add('is-content-transitioning');
    panelInner.style.minHeight = `${lockedHeight}px`;
    return lockedHeight;
  }

  releasePanelInnerHeight(panelInner, lockedHeight) {
    if (!panelInner || !this.isDesktopViewport() || lockedHeight == null) return;

    const targetHeight = panelInner.scrollHeight;

    if (targetHeight === lockedHeight) {
      panelInner.classList.remove('is-content-transitioning');
      panelInner.style.minHeight = '';
      return;
    }

    panelInner.style.minHeight = `${targetHeight}px`;

    const cleanup = () => {
      panelInner.classList.remove('is-content-transitioning');
      panelInner.style.minHeight = '';
      panelInner.removeEventListener('transitionend', onHeightTransitionEnd);
    };

    const onHeightTransitionEnd = event => {
      if (event.target !== panelInner || event.propertyName !== 'min-height') {
        return;
      }
      cleanup();
    };

    panelInner.addEventListener('transitionend', onHeightTransitionEnd);
    window.setTimeout(cleanup, 350);
  }

  swapContent(nextMarkup) {
    if (!this.contentEl) return Promise.resolve();

    const generation = ++this._transitionGeneration;

    if (this._contentTransition) {
      this._contentTransition = this._contentTransition
        .catch(() => {})
        .then(() => this.runContentTransition(nextMarkup, generation));
    } else {
      this._contentTransition = this.runContentTransition(
        nextMarkup,
        generation
      );
    }

    return this._contentTransition.finally(() => {
      if (this._transitionGeneration === generation) {
        this._contentTransition = null;
      }
    });
  }

  runContentTransition(nextMarkup, generation) {
    if (this.prefersReducedMotion()) {
      this.contentEl.innerHTML = nextMarkup;
      return Promise.resolve();
    }

    this.removeAttribute('loading');

    const panelInner = this.getPanelInner();
    const lockedHeight = this.lockPanelInnerHeight(panelInner);

    this.contentEl.classList.remove('predictive-search-content--enter');
    this.contentEl.classList.add('predictive-search-content--exit');

    return this.waitForOpacityTransition(this.contentEl).then(() => {
      if (generation !== this._transitionGeneration) return;

      this.contentEl.classList.remove('predictive-search-content--exit');
      this.contentEl.innerHTML = nextMarkup;
      this.contentEl.classList.add('predictive-search-content--enter');

      return new Promise(resolve => {
        requestAnimationFrame(() => {
          if (generation !== this._transitionGeneration) {
            resolve();
            return;
          }

          this.contentEl.classList.remove('predictive-search-content--enter');

          this.waitForOpacityTransition(this.contentEl).then(() => {
            if (generation !== this._transitionGeneration) return;
            this.releasePanelInnerHeight(panelInner, lockedHeight);
            resolve();
          });
        });
      });
    });
  }

  /**
   * Replaces the inner HTML of the content slot with the provided markup.
   */
  renderSearchResults(resultsMarkup) {
    if (!this.contentEl) return;

    const searchTerm = this.getQuery();

    this.swapContent(resultsMarkup).then(() => {
      if (!this.contentEl || this.getQuery() !== searchTerm) return;
      this.setAttribute('results', 'true');
      this.setLiveRegionResults();
      this.open();
    });
  }

  /**
   * Restores the empty state markup captured at construction time.
   */
  restoreEmptyState() {
    if (!this.contentEl || this.emptyStateMarkup == null) return;
    if (this.getQuery().length) return;

    this.swapContent(this.emptyStateMarkup).then(() => {
      if (this.getQuery().length) return;
      this.removeAttribute('results');
      this.removeAttribute('loading');
    });
  }

  setLiveRegionLoadingState() {
    if (!this.statusElement) return;
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countNode = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    if (countNode) this.setLiveRegionText(countNode.textContent);
  }

  open() {
    this.setAttribute('open', 'true');
    if (this.input) this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
      this.restoreEmptyState();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
    }
    this.removeAttribute('open');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
